import axios from 'axios';
import store from 'Store/clientStore';
import History from 'Store/history';
// To logout user when the error response is 401.
import { refreshToken, logoutUser, openModal, toggleOccupationPopup } from 'Store/actions';
import { SET_TRIBUN_USER_TO_BLOCKED, TRIBUN_ORIGINS } from 'Store/constants';
import { format } from 'date-fns';
import ProjectConfig from 'ProjectConfig';
import {
  getWebviewModeFlag,
  setWebviewModeFlag,
  isWebviewRenderType,
  hasNonWebviewRenderType,
} from 'Utils/webviewMode';
import { getUserTheme } from 'Utils/theme';
import LocalStorage from '../localStorage';
import { removeAuthToken } from './setAxiosAuthHeader';
import { isSuspendedMemberLogoutActive, beginSuspendedMemberLogout, shouldUseSuspendedLogoutOnForceAuthFailure } from '../suspendedMemberLogout';
const API_GATEWAY = ProjectConfig.API_GATEWAY;
let isRefreshing = false;
let unauthorizedQueue = [];
let webviewRefreshBlockedUntil = 0;
const WEBVIEW_REFRESH_COOLDOWN_MS = 10000;

const isWebviewMode = () => {
  if (getWebviewModeFlag()) {
    if (typeof window !== 'undefined' && hasNonWebviewRenderType(window.location.search)) {
      setWebviewModeFlag(false);
      return false;
    }
    return true;
  }
  if (typeof window === 'undefined') return false;
  const result = isWebviewRenderType(window.location.search);
  if (result) setWebviewModeFlag(true);
  return result;
};

const isApiGatewayRequest = url => !url || !url.startsWith('http') || url.startsWith(API_GATEWAY);

const setupThemeHeaderInterceptor = () => {
  axios.interceptors.request.use(config => {
    if (isApiGatewayRequest(config.url)) {
      config.headers['X-Client-Theme'] = getUserTheme();
    }
    return config;
  });
};

const authInterceptor = () => {
  setupThemeHeaderInterceptor();

  const isLocalhost = typeof window !== 'undefined'
    && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  if (!isLocalhost) {
    return setupResponseInterceptor();
  }

  const useProxy = !API_GATEWAY.includes('www.bilyoner.com');

  if (useProxy) {
    axios.interceptors.request.use(config => {
      if (!config.url) return config;

      if (config.url.startsWith('http')) {
        const urlObj = new URL(config.url);
        if (urlObj.hostname !== 'localhost' && urlObj.hostname !== '127.0.0.1') {
          config.url = config.url.replace(new URL(API_GATEWAY).origin, '');
        }
      } else if (config.url.startsWith('/') && !config.url.startsWith('/api/')) {
        config.url = '/api' + config.url;
      }

      return config;
    });
  }

  setupResponseInterceptor();
};

const setupResponseInterceptor = () => {

  axios.interceptors.response.use(
    res => {
      return res;
    },
    async err => {
      const { config } = err;
      const status = err.response?.status;
      const { code, domain, extras } = err.response?.data || {};
      const originalRequest = config;
      const { auth } = store.getState();
      const isWebview = isWebviewMode();

      // In mobile webview, if refresh recently failed, do not keep retrying refresh
      // on every 401 request. This prevents 401 storms and excessive API traffic.
      if (
        status === 401
        && isWebview
        && webviewRefreshBlockedUntil > Date.now()
        && !originalRequest?._retry
      ) {
        return Promise.reject(err);
      }

      if (status === 401 && !originalRequest._retry) {
        console.info('401 received...');
        if (originalRequest?.url?.includes('/v2/oauth-manager/users/login/refresh')) {
          if (!isWebview) forceLogoutAfterUnauthorized();
          return Promise.reject(err);
        }
        if (config.url === `${API_GATEWAY}/v2/mobile/tribun/user`) return Promise.reject(err);
        const requestUrl = config.url || '';
        const isLoggedOutBahista401 = !auth?.data?.refreshToken && (
          requestUrl.includes('/v2/authors/public/bahista')
          || requestUrl.includes('/sports-data/user/matches/v1/bahista/predictions')
        );
        if (isLoggedOutBahista401) return Promise.reject(err);
        if (!auth.data.refreshToken) {
          if (!isWebview) forceLogoutAfterUnauthorized();
          return Promise.reject(err);
        }

        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            unauthorizedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['X-Auth-Token'] = token;

            return axios(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;
        console.info('Refresh token logic has started...');
        try {
          const res = await store.dispatch(refreshToken());
          originalRequest.headers['X-Auth-Token'] = res?.accessToken;
          isRefreshing = false;
          webviewRefreshBlockedUntil = 0;
          retryUnauthorizedQueue(null, res.accessToken);
          return axios(originalRequest);
        } catch (err) {
          isRefreshing = false;
          retryUnauthorizedQueue(err, null);
          if (!isWebview) {
            forceLogoutAfterUnauthorized();
          } else {
            webviewRefreshBlockedUntil = Date.now() + WEBVIEW_REFRESH_COOLDOWN_MS;
            removeAuthToken();
          }
          return Promise.reject(err);
        }
      } else if (status === 403 && code === 4013 && domain === 'tribun-mobile-bff') {
        const isCouponSuccessPage = location.pathname.indexOf('/kupon-ozet') >= 0;
        let origin;
        if (location.pathname === '/') {
          origin = TRIBUN_ORIGINS.HOME;
        } else if (location.pathname.indexOf('/tribun') >= 0) {
          origin = TRIBUN_ORIGINS.TRIBUN;
        } else if (location.pathname.indexOf('/mac-karti') === -1) {
          origin = TRIBUN_ORIGINS.KICKOFF;
        }

        store.dispatch({
          type: SET_TRIBUN_USER_TO_BLOCKED,
        });

        if (origin) {
          store.dispatch(openModal(isCouponSuccessPage ? 'couponSuccessBlockedUserModal' : 'tribunBlockedUserModal', {
            date: format(new Date(extras.until), 'dd.MM.yyyy'),
            hours: format(new Date(extras.until), 'HH:mm'),
            origin: origin
          }));
          if (isCouponSuccessPage) LocalStorage.remove('tribunUser');
        }
      } else if (status === 400 && (( code === 4008 || code === 4009) && domain === 'internal-api-gateway' )) {
        store.dispatch(toggleOccupationPopup(true));
      }
      return Promise.reject(err);
    }
  );
};

// Unauthorized requests queue
const retryUnauthorizedQueue = (error, token = null) => {
  unauthorizedQueue.forEach(promise => {
    error ? promise.reject(error) : promise.resolve(token);
  });
  unauthorizedQueue = [];
};

function redirectToLogin () {
  if (isSuspendedMemberLogoutActive()) return;
  if (History.location.pathname !== '/giris-yap') {
    History.replace('/giris-yap');
  }
}

function forceLogoutAfterUnauthorized () {
  const auth = store.getState()?.auth;
  const useSuspendedModal = isSuspendedMemberLogoutActive()
    || shouldUseSuspendedLogoutOnForceAuthFailure(auth);

  if (useSuspendedModal) {
    beginSuspendedMemberLogout({
      openModal: name => store.dispatch(openModal(name))
    });
    return;
  }

  redirectToLogin();
  store.dispatch(logoutUser());
}
export default authInterceptor;
